'use client';

import { FC, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ENDPOINT_STATISTICS_BY_CLUSTER_SLUG } from '@/common/constants';
import { HttpApi } from '@/common/http';
import DatepickerControl from '@/components/controls/datepicker-control';
import SelectControl from '@/components/controls/select-control';
import { Form } from '@/components/ui/form';
import { usePartyStore } from '@/features/parties/store';
import { IDailyReport } from '../../models';

interface FormValues {
  cluster_id: string;
  selected_date: string | undefined;
}

const httpApi = new HttpApi();
export const DailyReport = () => {
  const form = useForm<FormValues>({
    defaultValues: {
      cluster_id: '',
      selected_date: undefined,
    },
  });
  const { t } = useTranslation();
  const { clusters, fetchClusters } = usePartyStore();
  const [total, setTotal] = useState<IDailyReport | null>(null);

  const clusterId = useWatch({
    name: 'cluster_id',
    control: form.control,
  });

  const selectedDate = useWatch({
    name: 'selected_date',
    control: form.control,
  });

  useEffect(() => {
    fetchClusters();
  }, []);

  const getDailyReport = async () => {
    try {
      const response = await httpApi.get<IDailyReport>(
        ENDPOINT_STATISTICS_BY_CLUSTER_SLUG.replace('{slug}', clusterId),
        {
          ...(selectedDate ? { selected_date: selectedDate } : {}),
        },
      );
      setTotal(response);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!clusterId) return;
    getDailyReport();
  }, [clusterId, selectedDate]);

  return (
    <Form {...form}>
      <form className="flex gap-4 items-center justify-between">
        <SelectControl
          name="cluster_id"
          options={clusters}
          label={t('claster')}
          control={form.control}
          className="space-y-0"
          selectClass="h-8 w-[220px]"
        />
        <DatepickerControl
          label={t('date')}
          name="selected_date"
          control={form.control}
          className="w-[220px] space-y-0"
          inputClass="h-8"
        />
        <ReportItem
          label={t('physical.weight')}
          value={total?.total_weight ?? 0}
        />
        <ReportItem
          label={t('conditioning.weight')}
          value={total?.total_weight_cond ?? 0}
        />
      </form>
    </Form>
  );
};

interface ReportItemProps {
  label: string;
  value: string | number | undefined;
}

const ReportItem: FC<ReportItemProps> = ({ label, value }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">
        {label} ({t('kg')})
      </span>
      <span className="text-sm">{value}</span>
    </div>
  );
};
